import {
  Component, OnInit, Output, EventEmitter, OnDestroy, Input,
  ChangeDetectorRef, ChangeDetectionStrategy, ViewChildren, QueryList
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';

import { forkJoin, throwError, of, combineLatest } from 'rxjs';
import { map, catchError, startWith, switchMap, debounceTime, distinctUntilChanged, first } from 'rxjs/operators';

import { RecordService } from 'modules/core/services/record.service';
import { CustomerService } from 'modules/core/services/customer.service';

import { DialogService } from 'modules/shared/components/dialog/dialog.service';

import { ReplaySubject, Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { RecordType } from 'modules/shared/models/record.models';
import { IndividualRecord, CorporateRecord, Record, DetailFormOption } from 'modules/shared/models/record.models';
import { RecordsResponse } from 'store/models/record.models';

import { RequireMatch } from 'modules/shared/validators/requireMatch';

import { ICrpDataForDetailsForm } from '../../pages/crp-edit/crp-edit.page';

import { MultipleValuesInputComponent } from './multiple-values-input/multiple-values-input.component';

import { AdminService } from 'modules/core/services/admin.service';

@Component({
  selector: 'artemis-details-form',
  templateUrl: './details-form.component.html',
  styleUrls: ['./details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe]
})
export class DetailsFormComponent implements OnInit, OnDestroy {

  @ViewChildren('multipleValuesInputComponent') multipleValuesInputComponents: QueryList<MultipleValuesInputComponent>;

  @Input() isSearch: boolean = false;

  @Input() isPrimary: boolean;
  @Input() detailFormOption: DetailFormOption;

  @Input() set dataAvaliableFormFields(data: any) {
    if (data) {
      this.dataAvailableFormFields = data;
      if (data.jsonValue.annualIncomeLevel && data.jsonValue.annualIncomeLevel.visible) {
        this.isShowAnnualIncomeLevel = true;
        if (data.jsonValue.annualIncomeLevel.required) {
          this.isRequiredAnnualIncomeLevel = true;
          this.individualFormGroup.addControl('annualIncomeLevel', new FormControl('', Validators.required));
        } else {
          this.isRequiredAnnualIncomeLevel = false;
          this.individualFormGroup.addControl('annualIncomeLevel', new FormControl(''));
        }
        this.initIndividualFormAutocompleteFilters();
      } else {
        this.isShowAnnualIncomeLevel = false;
        this.individualFormGroup.patchValue({ annualIncomeLevel: '' });
      }
      if (data.jsonValue.sourceOfWealth && data.jsonValue.sourceOfWealth.visible) {
        this.isShowSourceOfWealth = true;
        if (data.jsonValue.sourceOfWealth.required) {
          this.isRequiredSourceOfWealth = true;
          this.individualFormGroup.addControl('sourceOfWealth', new FormControl([], Validators.required));
        } else {
          this.isRequiredSourceOfWealth = false;
          this.individualFormGroup.addControl('sourceOfWealth', new FormControl([]));
        }
        this.initCorporateFormAutocompleteFilters();
      } else {
        this.isShowSourceOfWealth = false;
        this.individualFormGroup.patchValue({ sourceOfWealth: [] });
      }
      if (this.isPrimary) {
        if (data.jsonValue.policyCount && data.jsonValue.policyCount.visible) {
          this.isShowPolicyCount = true;
          this.policyCountList = data.jsonValue.policyCount.allowedValues;
          if (data.jsonValue.policyCount.required) {
            this.isRequiredPolicyCount = true;
            this.individualFormGroup.addControl('policyCount', new FormControl('', [Validators.required, , RequireMatch.bind(this)('policyCountList')]));
            this.corporateFormGroup.addControl('policyCount', new FormControl('', [Validators.required, , RequireMatch.bind(this)('policyCountList')]));
          } else {
            this.isRequiredPolicyCount = false;
            this.individualFormGroup.addControl('policyCount', new FormControl(''));
            this.corporateFormGroup.addControl('policyCount', new FormControl(''));
          }
        } else {
          this.isShowPolicyCount = false;
          this.individualFormGroup.patchValue({ policyCount: 'null' });
        }
        if (data.jsonValue.premiumAmount && data.jsonValue.premiumAmount.visible) {
          this.isShowPremiumAmount = true;
          this.premiumAmountList = data.jsonValue.premiumAmount.allowedValues;
          if (data.jsonValue.premiumAmount.required) {
            this.isRequiredPremiumAmount = true;
            this.individualFormGroup.addControl('premiumAmount', new FormControl('', [Validators.required, , RequireMatch.bind(this)('premiumAmountList')]));
            this.corporateFormGroup.addControl('premiumAmount', new FormControl('', [Validators.required, , RequireMatch.bind(this)('premiumAmountList')]));
          } else {
            this.isRequiredPremiumAmount = false;
            this.individualFormGroup.addControl('premiumAmount', new FormControl(''));
            this.corporateFormGroup.addControl('premiumAmount', new FormControl(''));
          }
        } else {
          this.isShowPremiumAmount = false;
          this.individualFormGroup.patchValue({ premiumAmount: 'null' });
        }
      }
      this.cdRef.detectChanges();
      this.loadLists();
    }
  }

  @Input() set customerCrpData(value: ICrpDataForDetailsForm) {
    this.data = value;
    if (this.data && this.data.customer && this.data.record) {
      this.corporateFormGroup.reset();
      this.individualFormGroup.reset();

      const formData = Object.assign({}, {
        ...value.customer,
        'customerId': value.customer.id,
        'customerReferenceId': value.customer.referenceId
      },
        {
          ...value.record,
          'recordId': value.record.id,
          'recordReferenceId': value.record.referenceId
        }
      );

      delete formData.id;
      delete formData.referenceId;

      this.customerType = <RecordType>formData.recordType;

      if (formData.recordType === RecordType.CORPORATE) {
        this.corporateFormGroup.patchValue(formData);
        this.corporateFormGroup.patchValue({ status: this.data.crpStatus });
        this.corporateFormGroup.markAsPristine();
      } else {
        this.individualFormGroup.patchValue(formData);
        this.individualFormGroup.patchValue({ status: this.data.crpStatus });
        this.individualFormGroup.markAsPristine();
      }
      this.cdRef.detectChanges();
      this.data$.next(value);
    }
  }

  @Output() formDataChanged: any = new EventEmitter();
  @Output() loading: EventEmitter<boolean> = new EventEmitter();

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject(1);

  data: ICrpDataForDetailsForm;
  data$: Subject<ICrpDataForDetailsForm> = new Subject();
  sourcesOfFundsLoaded$: Subject<boolean> = new Subject();

  customerType: RecordType = RecordType.INDIVIDUAL;

  recordsSuggest: Array<IndividualRecord | CorporateRecord | Record>;
  offsetScrollRecordsSuggest: number = 0;
  currentRecord: IndividualRecord | CorporateRecord | Record;

  individualFormGroup: FormGroup;
  corporateFormGroup: FormGroup;
  maxDoBDate: Date = new Date(new Date().getTime() - 16 * 365.25 * 24 * 3600 * 1000);
  minDoBDate: Date = new Date(new Date().getTime() - 120 * 365.25 * 24 * 3600 * 1000);
  maxIncorporationDate: Date = new Date();
  maxIdentityIssueDate: Date = new Date();
  maxIdentityExpiryDate: Date = new Date(new Date().getTime() + 25 * 365.25 * 24 * 3600 * 1000);
  showRemoveBtn: boolean = false;
  isShowSourceOfWealth: boolean = false;
  isShowAnnualIncomeLevel: boolean = false;
  isShowPolicyCount: boolean = false;
  isShowPremiumAmount: boolean = false;
  isRequiredSourceOfWealth: boolean;
  isRequiredAnnualIncomeLevel: boolean;
  isRequiredPolicyCount: boolean;
  isRequiredPremiumAmount: boolean;
  dataAvailableFormFields: any;

  // Data for populating UI
  readonly gendersList: Array<string> = ['MALE', 'FEMALE', 'UNKNOWN'];
  readonly statusesList: Array<string> = ['CURRENT', 'RESIGNED'];
  countriesList: Array<string>;
  industriesList: Array<string>;
  industriesMigrationList: Array<string>;
  onboardingModesList: Array<string>;
  paymentModesList: Array<string>;
  productServiceComplexitiesList: Array<string>;
  sourcesOfFundsList: Array<string>;
  occupationsList: Array<string>;
  occupationsMigrationList: Array<string>;
  identityDocumentTypesList: Array<string>;
  titlesList: Array<string>;
  ownershipStructureLayersList: Array<string>;
  entityTypesList: Array<string>;
  sourceFundsList: Array<string>;
  sourceOfWealthList: Array<string>;
  annualIncomeLevelsList: Array<string>;
  policyCountList: Array<string>;
  premiumAmountList: Array<string>;

  filteredIndividualNationalities$: Observable<Array<string>>;
  filteredIndividualCountriesOfResidence$: Observable<Array<string>>;
  filteredIndividualCountriesOfBirth$: Observable<Array<string>>;
  filteredIndividualIndustries$: Observable<Array<string>>;
  filteredIndividualOccupations$: Observable<Array<string>>;
  filteredIndividualAnnualIncomeLevel$: Observable<Array<string>>;

  filteredCorporateOperationsCountries$: Observable<Array<string>>;
  filteredCorporateCountriesOfResidence$: Observable<Array<string>>;
  filteredCorporateOccupations$: Observable<Array<string>>;


  constructor(private formBuilder: FormBuilder,
    private recordService: RecordService,
    private customerService: CustomerService,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private adminService: AdminService,
    private datePipe: DatePipe) { }

  ngOnInit() {
    this.loading.emit(true);
    this.loadLists();
    this.sourceCustomerDataLoadedCallback();
    this.buildIndividualForm();
    this.buildCorporateForm();
    this.emitDataToParent();
    this.onChanges();
    this.initNameSearch();
    this.setAgeLimitRestriction();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * @method sourceCustomerDataLoadedCallback
   * Subscription to check if data and sources of funds are ready
   */
  private sourceCustomerDataLoadedCallback(): void {
    combineLatest([
      this.data$,
      this.sourcesOfFundsLoaded$
    ])
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        () => {
          this.checkOtherSrcOfFunds();
          this.checkOtherIDTypes();
          if (this.data.record.id) {
            this.individualFormGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
            this.corporateFormGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
            this.markFormGroupTouched(this.individualFormGroup);
            this.markFormGroupTouched(this.corporateFormGroup);
            this.cdRef.detectChanges();
          }
        }
      );
  }

  /**
   * @method loadLists
   * Loads all data for populating select lists
   */
  private loadLists(): void {
    const Apis = [
      this.recordService.getCountries(),
      this.recordService.getSSIC(),
      this.customerService.getOnboardingModes(),
      this.customerService.getPaymentModes(),
      this.customerService.getProductComplexities(),
      this.recordService.getSourceOfFunds(),
      this.recordService.getSSOC(),
      this.recordService.getIDTypes(),
      this.recordService.getTitles(),
      this.recordService.getOwnershipLayers(),
      this.recordService.getEntityTypes(),
      this.customerService.getCrpsRole(),
      this.isShowSourceOfWealth ? this.customerService.getSourceOfWealth() : of(null),
      this.isShowAnnualIncomeLevel ? this.customerService.getAnnualIncomeLevelUsd() : of(null),
      this.recordService.getSSICMigration(),
      this.recordService.getSSOCMigration()
    ];

    forkJoin(Apis).pipe(
      map(
        ([countries, ssic, onboarding, payment, product, sourceOfFund, ssoc, idType, titles, layers, entityTypes, roles, sourceOfWealths,
          annualIncomeLevels, ssicMigration, ssocMigration]:
          [Array<string>, Array<string>, Array<string>, Array<string>, Array<string>, Array<string>, Array<string>, Array<string>,
            Array<string>, Array<string>, Array<string>, Array<string>, Array<string>, Array<string>, Array<string>, Array<string>]
        ) => {
          return {
            countries, ssic, onboarding, payment, product, sourceOfFund, ssoc, idType, titles, layers, entityTypes, roles, sourceOfWealths,
            annualIncomeLevels, ssicMigration, ssocMigration
          };
        }
      ),
      catchError((err: any) => {
        return throwError(err);
      }),
      takeUntil(this.unsubscribe$)
    )
      .subscribe(
        (res: any) => {
          this.countriesList = res.countries;
          this.industriesList = res.ssic;
          this.onboardingModesList = res.onboarding;
          this.paymentModesList = res.payment;
          this.productServiceComplexitiesList = res.product;
          this.sourcesOfFundsList = res.sourceOfFund;
          this.occupationsList = res.ssoc;
          this.identityDocumentTypesList = res.idType;
          this.titlesList = res.titles;
          this.ownershipStructureLayersList = res.layers;
          this.entityTypesList = res.entityTypes;
          this.sourceFundsList = res.sourceOfFund;
          if (res.sourceOfWealths) {
            this.sourceOfWealthList = res.sourceOfWealths;
          }
          if (res.annualIncomeLevels) {
            this.annualIncomeLevelsList = res.annualIncomeLevels;
          }
          this.industriesMigrationList = res.ssicMigration;
          this.occupationsMigrationList = res.ssocMigration;
          this.sourcesOfFundsLoaded$.next(res.sourceOfFund);
          this.initIndividualFormAutocompleteFilters();
          this.initCorporateFormAutocompleteFilters();
          this.loading.emit(false);
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * @method initNameSearch
   * Inits search by duplications names
   */
  private initNameSearch(): void {
    if (this.isSearch) {
      this.individualFormGroup
        .get('name')
        .valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(300),
          switchMap((value: string) => {
            if (value && !this.showRemoveBtn) {
              if (this.detailFormOption && this.detailFormOption.exclude_customer_id) {
                return this.recordService.getIndividualRecords(
                  {
                    search: value,
                    exclude_customer_id: this.detailFormOption.exclude_customer_id
                  }
                );
              } else {
                return this.recordService.getIndividualRecords({ search: value });
              }
            } else {
              return of([]);
            }
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe(
          (records: RecordsResponse) => {
            this.offsetScrollRecordsSuggest = 0;
            this.recordsSuggest = records.results;
            this.cdRef.detectChanges();
          }
        );

      this.corporateFormGroup
        .get('name')
        .valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(300),
          switchMap((value: string) => {
            if (value && !this.showRemoveBtn) {
              if (this.detailFormOption && this.detailFormOption.exclude_customer_id) {
                return this.recordService.getCorporateRecords(
                  {
                    search: value,
                    exclude_customer_id: this.detailFormOption.exclude_customer_id
                  }
                );
              } else {
                return this.recordService.getCorporateRecords({ search: value });
              }
            } else {
              return of([]);
            }
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe(
          (records: RecordsResponse) => {
            this.offsetScrollRecordsSuggest = 0;
            this.recordsSuggest = records.results;
            this.cdRef.detectChanges();
          }
        );
    }
  }

  /**
   * @method getRecordsScroll
   * Get recordsSuggest When Scroll
   */
  private getRecordsScroll(): Observable<any> {
    if (this.customerType === RecordType.INDIVIDUAL) {
      const value = this.individualFormGroup.get('name').value;
      if (value) {
        if (this.detailFormOption && this.detailFormOption.exclude_customer_id) {
          return this.recordService.getIndividualRecords(
            {
              search: value,
              exclude_customer_id: this.detailFormOption.exclude_customer_id,
              offset: this.offsetScrollRecordsSuggest
            }
          );
        } else {
          return this.recordService.getIndividualRecords(
            {
              search: value,
              offset: this.offsetScrollRecordsSuggest
            }
          );
        }
      } else {
        return of([]);
      }
    } else if (this.customerType === RecordType.CORPORATE) {
      const value = this.corporateFormGroup.get('name').value;
      if (value) {
        if (this.detailFormOption && this.detailFormOption.exclude_customer_id) {
          return this.recordService.getCorporateRecords(
            {
              search: value,
              exclude_customer_id: this.detailFormOption.exclude_customer_id,
              offset: this.offsetScrollRecordsSuggest
            }
          );
        } else {
          return this.recordService.getCorporateRecords(
            {
              search: value,
              offset: this.offsetScrollRecordsSuggest
            }
          );
        }
      } else {
        return of([]);
      }
    }
  }

  /**
   * @method onScroll
   * Scroll Event Listener
   */
  onScroll(offsetScrollRecordsSuggest: number): void {
    if ((offsetScrollRecordsSuggest > this.offsetScrollRecordsSuggest) && !this.showRemoveBtn) {
      this.offsetScrollRecordsSuggest = offsetScrollRecordsSuggest;
      this.getRecordsScroll()
        .pipe(
          takeUntil(this.unsubscribe$)
        )
        .subscribe(
          (records: RecordsResponse) => {
            if (records && records.results) {
              this.recordsSuggest = this.recordsSuggest.concat(records.results);
              this.cdRef.detectChanges();
            }
          }
        );
    }
  }

  /**
   * @method removeScrollEventListener
   * Remove Scroll Event Listener
   */

  /**
   * @method buildIndividualForm
   * Build individual form
   */
  private buildIndividualForm(): void {
    const individualForm = {
      isActiveCustomer: [true],
      title: ['', RequireMatch.bind(this)('titlesList')],
      name: [, Validators.required],
      aliasNames: [[]],
      oldNames: [[]],
      gender: ['', [Validators.required, RequireMatch.bind(this)('gendersList')]],
      nationality: ['', [Validators.required, RequireMatch.bind(this)('countriesList')]],
      countryOfResidence: ['', [Validators.required, RequireMatch.bind(this)('countriesList')]],
      idType: ['', RequireMatch.bind(this)('identityDocumentTypesList')],
      otherIdType: [''],
      idNumber: [],
      idIssueDate: [],
      idExpiryDate: [],
      countryOfBirth: ['', RequireMatch.bind(this)('countriesList')],
      sourceOfFunds: [''],
      otherSourceOfFunds: [],
      customerReferenceId: [],
      recordReferenceId: [],
      dateOfBirth: [],
      addresses: [[]],
      phoneNumbers: [[]],
      emailAddresses: [[]],
      bankAccounts: [[]],
      natureOfBusinessRelationship: [],
      isUndischargedBankrupt: [false],
    };
    if (this.isPrimary) {
      individualForm['onboardingMode'] = [, [Validators.required, RequireMatch.bind(this)('onboardingModesList')]];
      individualForm['paymentModes'] = [[], Validators.required];
      individualForm['productServiceComplexity'] = ['', [Validators.required, RequireMatch.bind(this)('productServiceComplexitiesList')]];
      individualForm['industry'] = ['', [Validators.required, RequireMatch.bind(this)('industriesMigrationList')]];
      individualForm['occupation'] = ['', [Validators.required, RequireMatch.bind(this)('occupationsMigrationList')]];
    } else {
      individualForm['status'] = [, [Validators.required]];
    }
    this.individualFormGroup = this.formBuilder.group(individualForm);

    if (this.individualFormGroup.get('paymentModes')) {
      this.individualFormGroup
        .get('paymentModes')
        .valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (value: Array<string>) => {
            if (value) {
              if (value.indexOf('UNKNOWN') >= 0) {
                this.individualFormGroup.get('paymentModes').setValue(['UNKNOWN'], { emitEvent: false });
              } else if (value.indexOf('NOT APPLICABLE') >= 0) {
                this.individualFormGroup.get('paymentModes').setValue(['NOT APPLICABLE'], { emitEvent: false });
              }
            }
          }
        );
    }
    if (this.isPrimary) {
      this.patchIndividualValues();
    } else {
      this.patchIndividualCRPValues();
    }
  }

  /**
   * @method initIndividualFormAutocompleteFilters
   * Inits filters for autocompletes in individual form
   */
  private initIndividualFormAutocompleteFilters(): void {
    this.filteredIndividualNationalities$ = this.individualFormGroup.get('nationality').valueChanges.pipe(
      startWith(''),
      map((filterInput: string) => this.filterContains(filterInput, this.countriesList)),
      takeUntil(this.unsubscribe$)
    );

    this.filteredIndividualCountriesOfResidence$ = this.individualFormGroup.get('countryOfResidence').valueChanges.pipe(
      startWith(''),
      map((filterInput: string) => this.filterContains(filterInput, this.countriesList)),
      takeUntil(this.unsubscribe$)
    );

    this.filteredIndividualCountriesOfBirth$ = this.individualFormGroup.get('countryOfBirth').valueChanges.pipe(
      startWith(''),
      map((filterInput: string) => this.filterContains(filterInput, this.countriesList)),
      takeUntil(this.unsubscribe$)
    );

    if (this.isPrimary) {
      this.filteredIndividualOccupations$ = this.individualFormGroup.get('occupation').valueChanges.pipe(
        startWith(''),
        map((filterInput: string) => this.filterContains(filterInput, this.occupationsList, 100)),
        takeUntil(this.unsubscribe$)
      );

      this.filteredIndividualIndustries$ = this.individualFormGroup.get('industry').valueChanges.pipe(
        startWith(''),
        map((filterInput: string) => this.filterContains(filterInput, this.industriesList, 100)),
        takeUntil(this.unsubscribe$)
      );
      if (this.isShowAnnualIncomeLevel) {
        this.filteredIndividualAnnualIncomeLevel$ = this.individualFormGroup.get('annualIncomeLevel').valueChanges.pipe(
          startWith(''),
          map((filterInput: string) => this.filterContains(filterInput, this.annualIncomeLevelsList)),
          takeUntil(this.unsubscribe$)
        );
      }
    }
  }

  /**
   * @method buildCorporateForm
   * Build corporate form
   */
  private buildCorporateForm(): void {
    const corporateForm = {
      isActiveCustomer: [true],
      isIncorporated: [true],
      name: [, Validators.required],
      aliasNames: [[]],
      oldNames: [[]],
      entityType: [, [Validators.required, RequireMatch.bind(this)('entityTypesList')]],
      ownershipStructureLayers: [, [Validators.required, RequireMatch.bind(this)('ownershipStructureLayersList')]],
      countryOfIncorporation: [, [Validators.required, RequireMatch.bind(this)('countriesList')]],
      countryOfOperations: [, [Validators.required, RequireMatch.bind(this)('countriesList')]],
      sourceOfFunds: [, RequireMatch.bind(this)('sourcesOfFundsList')],
      otherSourceOfFunds: [],
      customerReferenceId: [],
      recordReferenceId: [],
      incorporationNumber: [],
      incorporationExpiryDate: [],
      website: [],
      incorporationDate: [],
      addresses: [[]],
      phoneNumbers: [[]],
      emailAddresses: [[]],
      bankAccounts: [[]],
      natureOfBusinessRelationship: [],
      isUndischargedBankrupt: [false],
      inFatfJurisdiction: [false],
      imoNumber: []
    };

    if (this.isPrimary) {
      corporateForm['onboardingMode'] = [, [Validators.required, RequireMatch.bind(this)('onboardingModesList')]];
      corporateForm['paymentModes'] = [[], Validators.required];
      corporateForm['productServiceComplexity'] = [, [Validators.required, RequireMatch.bind(this)('productServiceComplexitiesList')]];
      corporateForm['businessActivity'] = [, [Validators.required, RequireMatch.bind(this)('industriesMigrationList')]];
    } else {
      corporateForm['status'] = [, [Validators.required, RequireMatch.bind(this)('statusesList')]];
    }

    this.corporateFormGroup = this.formBuilder.group(corporateForm);

    if (this.corporateFormGroup.get('paymentModes')) {
      this.corporateFormGroup
        .get('paymentModes')
        .valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (value: Array<string>) => {
            if (value) {
              if (value.indexOf('UNKNOWN') >= 0) {
                this.corporateFormGroup.get('paymentModes').setValue(['UNKNOWN'], { emitEvent: false });
              } else if (value.indexOf('NOT APPLICABLE') >= 0) {
                this.corporateFormGroup.get('paymentModes').setValue(['NOT APPLICABLE'], { emitEvent: false });
              }
            }
          }
        );
    }
    if (this.isPrimary) {
      this.patchCorporateValues();
    } else {
      this.patchCorporateCRPValues();
    }
  }

  /**
   * @method initCorporateFormAutocompleteFilters
   * Inits filters for autocompletes in corporate form
   */
  private initCorporateFormAutocompleteFilters(): void {
    this.filteredCorporateOperationsCountries$ = this.corporateFormGroup.get('countryOfOperations').valueChanges.pipe(
      startWith(''),
      map((filterInput: string) => this.filterContains(filterInput, this.countriesList)),
      takeUntil(this.unsubscribe$)
    );

    this.filteredCorporateCountriesOfResidence$ = this.corporateFormGroup.get('countryOfIncorporation').valueChanges.pipe(
      startWith(''),
      map((filterInput: string) => this.filterContains(filterInput, this.countriesList)),
      takeUntil(this.unsubscribe$)
    );

    if (this.isPrimary) {
      this.filteredCorporateOccupations$ = this.corporateFormGroup.get('businessActivity').valueChanges.pipe(
        startWith(''),
        map((filterInput: string) => this.filterContains(filterInput, this.industriesList, 100)),
        takeUntil(this.unsubscribe$)
      );
    }
  }

  /**
   * @method onChanges
   * Init subscription to form changes
   */
  private onChanges(): void {
    this.individualFormGroup
      .valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.emitDataToParent());

    this.corporateFormGroup
      .valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.emitDataToParent());
  }

  /**
   * @method emitDataToParent
   * Emits data output
   */
  private emitDataToParent(): void {
    if (this.individualFormGroup.value.sourceOfFunds === 'OTHERS' && this.individualFormGroup.value.otherSourceOfFunds) {
      this.individualFormGroup.value.sourceOfFunds = this.individualFormGroup.value.otherSourceOfFunds;
    }

    if (this.corporateFormGroup.value.sourceOfFunds === 'OTHERS' && this.corporateFormGroup.value.otherSourceOfFunds) {
      this.corporateFormGroup.value.sourceOfFunds = this.corporateFormGroup.value.otherSourceOfFunds;
    }

    if (this.individualFormGroup.value.idType === 'OTHERS' && this.individualFormGroup.value.otherIdType) {
      this.individualFormGroup.value.idType = this.individualFormGroup.value.otherIdType;
    }

    this.individualFormGroup.value.dateOfBirth =
      this.datePipe.transform(this.individualFormGroup.value.dateOfBirth, 'yyyy-MM-dd');
    this.individualFormGroup.value.idIssueDate =
      this.datePipe.transform(this.individualFormGroup.value.idIssueDate, 'yyyy-MM-dd');
    this.individualFormGroup.value.idExpiryDate =
      this.datePipe.transform(this.individualFormGroup.value.idExpiryDate, 'yyyy-MM-dd');
    this.corporateFormGroup.value.incorporationDate =
      this.datePipe.transform(this.corporateFormGroup.value.incorporationDate, 'yyyy-MM-dd');
    this.corporateFormGroup.value.incorporationExpiryDate =
      this.datePipe.transform(this.corporateFormGroup.value.incorporationExpiryDate, 'yyyy-MM-dd');

    this.formDataChanged.emit({
      individualFormObject: this.individualFormGroup,
      corporateFormObject: this.corporateFormGroup,
      individualForm: this.individualFormGroup.value,
      corporateForm: this.corporateFormGroup.value,
      customerType: this.customerType,
      corporateFormValid: this.corporateFormGroup.valid,
      individualFormValid: this.individualFormGroup.valid,
      recordId: this.currentRecord ? this.currentRecord.id : null
    });
  }

  /**
   * @method filterStartWith
   * Method for custom sorting in autocomplete
   */
  // private filterStartWith(value: string, options: Array<string>, count?: number): Array<string> {
  //   if (value) {
  //     const filterValue: string = value.toLowerCase();
  //     let filteredArray: Array<string> = options.filter((option: string) => option.toLowerCase().indexOf(filterValue) === 0);
  //     if (count) {
  //       filteredArray = filteredArray.slice(0, count);
  //     }

  //     return filteredArray;
  //   } else {
  //     return count ? options.slice(0, count) : options;
  //   }
  // }

  /**
   * @method filterContains
   * Method for custom sorting in autocomplete
   */
  private filterContains(value: string, options: Array<string>, count?: number): Array<string> {
    if (value) {
      const filterValue: string = value.toLowerCase();
      let filteredArray: Array<string> = options.filter((option: string) => option.toLowerCase().includes(filterValue));
      if (count) {
        filteredArray = filteredArray.slice(0, count);
      }

      return filteredArray;
    } else {
      return count ? options.slice(0, count) : options;
    }
  }

  /**
   * @method updateRecord
   * Update current record after selecting from name autocomplete
   */
  private updateRecord(record: any): void {
    this.recordsSuggest = [];
    this.currentRecord = record;
    if (record && this.customerType === RecordType.INDIVIDUAL) {
      this.showRemoveBtn = true;
      this.individualFormGroup.patchValue({
        title: record.title,
        name: record.name,
        aliasNames: record.aliasNames || [],
        oldNames: record.oldNames || [],
        gender: record.gender,
        nationality: record.nationality,
        countryOfResidence: record.countryOfResidence,
        idType: record.idType,
        idNumber: record.idNumber,
        idIssueDate: this.datePipe.transform(record.idIssueDate, 'yyyy-MM-dd'),
        idExpiryDate: this.datePipe.transform(record.idExpiryDate, 'yyyy-MM-dd'),
        countryOfBirth: record.countryOfBirth,
        sourceOfFunds: record.sourceOfFunds,
        dateOfBirth: this.datePipe.transform(record.dateOfBirth, 'yyyy-MM-dd'),
        occupation: record.occupation,
        industry: record.industry,
        recordReferenceId: record.referenceId,
        addresses: record.addresses || [],
        phoneNumbers: record.phoneNumbers || [],
        emailAddresses: record.emailAddresses || [],
        bankAccounts: record.bankAccounts || [],
        isUndischargedBankrupt: record.isUndischargedBankrupt,
        sourceOfWealth: record.sourceOfWealth,
        annualIncomeLevel: record.annualIncomeLevel
      });

      this.markFormGroupTouched(this.individualFormGroup);
    } else if (record && this.customerType === RecordType.CORPORATE) {
      this.showRemoveBtn = true;
      this.corporateFormGroup.patchValue({
        isIncorporated: record.isIncorporated,
        name: record.name,
        aliasNames: record.aliasNames || [],
        oldNames: record.oldNames || [],
        entityType: record.entityType,
        ownershipStructureLayers: record.ownershipStructureLayers,
        countryOfIncorporation: record.countryOfIncorporation,
        countryOfOperations: record.countryOfOperations,
        sourceOfFunds: record.sourceOfFunds,
        businessActivity: record.businessActivity,
        incorporationNumber: record.incorporationNumber,
        website: record.website,
        incorporationDate: this.datePipe.transform(record.incorporationDate, 'yyyy-MM-dd'),
        incorporationExpiryDate: this.datePipe.transform(record.incorporationExpiryDate, 'yyyy-MM-dd'),
        recordReferenceId: record.referenceId,
        addresses: record.addresses || [],
        phoneNumbers: record.phoneNumbers || [],
        emailAddresses: record.emailAddresses || [],
        bankAccounts: record.bankAccounts || [],
        isUndischargedBankrupt: record.isUndischargedBankrupt,
        inFatfJurisdiction: record.inFatfJurisdiction,
        imoNumber: record.imoNumber
      });

      this.markFormGroupTouched(this.corporateFormGroup);
    }
    this.checkOtherSrcOfFunds();
    this.checkOtherIDTypes();
    this.cdRef.detectChanges();
  }

  /**
   * @method checkOtherSrcOfFunds
   * Changes source of funds to correct format
   */
  private checkOtherSrcOfFunds(): void {
    const dataRecord: IndividualRecord | CorporateRecord | Record = this.currentRecord ? this.currentRecord : this.data.record;
    const isOtherSrcOfFund: boolean = dataRecord.sourceOfFunds && (dataRecord.sourceOfFunds === 'OTHERS' ||
      !~this.sourceFundsList.indexOf(dataRecord.sourceOfFunds));

    if (isOtherSrcOfFund) {
      this.corporateFormGroup.patchValue({
        'sourceOfFunds': 'OTHERS',
        'otherSourceOfFunds': dataRecord.sourceOfFunds === 'OTHERS' ? '' : dataRecord.sourceOfFunds
      });

      this.individualFormGroup.patchValue({
        'sourceOfFunds': 'OTHERS',
        'otherSourceOfFunds': dataRecord.sourceOfFunds === 'OTHERS' ? '' : dataRecord.sourceOfFunds
      });
    }
  }

  /**
   * @method checkOtherIDTypes
   * Changes id types to correct format
   */
  private checkOtherIDTypes(): void {
    if (this.customerType === RecordType.INDIVIDUAL) {
      const individualRecord: IndividualRecord = <IndividualRecord>(this.currentRecord ? this.currentRecord : this.data.record);
      const otherIdType: boolean = !!individualRecord.idType && (individualRecord.idType === 'OTHERS' ||
        !~this.identityDocumentTypesList.indexOf(individualRecord.idType));
      if (otherIdType) {
        this.individualFormGroup.patchValue({
          'idType': 'OTHERS',
          'otherIdType': individualRecord.idType === 'OTHERS' ? '' : individualRecord.idType
        });
      }
    }
  }

  /**
   * @method setAgeLimitRestriction
   * Retrieve Age Limit config from AppConfig
   */
  private setAgeLimitRestriction(): void {
    this.adminService.getConfig('AGE_LIMIT')
      .pipe(
        first()
      ).subscribe(
        res => {
          const ageLimit = +res.value;
          this.maxDoBDate = new Date();
          this.maxDoBDate.setFullYear(this.maxDoBDate.getFullYear() - ageLimit);
        }
      );
  }

  /**
   * @method resetForms
   * Reset forms
   */
  resetForms(): void {
    this.individualFormGroup.reset();
    this.corporateFormGroup.reset();
    this.recordsSuggest = [];
    this.showRemoveBtn = false;
    this.currentRecord = null;
    if (this.multipleValuesInputComponents.length) {
      this.multipleValuesInputComponents.forEach((multipleValuesInputComponent: MultipleValuesInputComponent) => {
        multipleValuesInputComponent.form.get('value').setValue('');
      });
    }
    if (this.customerType === RecordType.INDIVIDUAL) {
      this.individualFormGroup.patchValue({ isActiveCustomer: true, isUndischargedBankrupt: false });
      if (this.isPrimary) {
        this.patchIndividualValues();
      } else {
        this.patchIndividualCRPValues();
      }
    } else {
      this.corporateFormGroup.patchValue({
        isIncorporated: true,
        isActiveCustomer: true,
        isUndischargedBankrupt: false,
        inFatfJurisdiction: false
      });
      if (this.isPrimary) {
        this.patchCorporateValues();
      } else {
        this.patchCorporateCRPValues();
      }
    }
    this.individualFormGroup.markAsPristine();
    this.corporateFormGroup.markAsPristine();
  }

  /**
   * @method removeSelectedCustomer
   * Remove customer if was selected from name autocomplete
   */
  removeSelectedCustomer(): void {
    this.resetForms();
    this.cdRef.detectChanges();
  }

  /**
   * @method optionSelected
   * Select option from name suggestions
   */
  optionSelected(record: Record) {
    this.updateRecord(record);
    this.cdRef.detectChanges();
  }

  /**
   * @method markFormGroupTouched
   * Marks formgroup touched
   */
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control: any) => {
      control.markAsTouched();
      if (control.controls) {
        control.controls.forEach((childControl: FormControl) => childControl.markAsTouched());
      }
    });
  }

  /**
   * @method corporateFormMultipleValuesInputHandler
   * Handles when multiple values formcontrol fired
   */
  corporateFormMultipleValuesInputHandler(values: Array<string>, name: string): void {
    this.corporateFormGroup.get(name).setValue(values);
    this.emitDataToParent();
  }

  /**
   * @method individualFormMultipleValuesInputHandler
   * Handles when multiple values formcontrol fired
   */
  individualFormMultipleValuesInputHandler(values: Array<string>, name: string): void {
    this.individualFormGroup.get(name).setValue(values);
    this.emitDataToParent();
  }

  /**
   * @method changeToggleType
   * Handles changing customer type
   */
  changeToggleType(): void {
    this.recordsSuggest = [];
    if (this.individualFormGroup.dirty || this.corporateFormGroup.dirty) {
      this.dialogService
        .confirm('By changing the customer type you will lose all your input data, are you sure you want to change customer type?',
          'NOTICE', false, true)
        .subscribe(
          (res: string) => {
            if (res === 'submit') {
              this.emitDataToParent();
              this.resetForms();

            } else {
              if (this.customerType === RecordType.CORPORATE) {
                this.customerType = RecordType.INDIVIDUAL;
              } else {
                this.customerType = RecordType.CORPORATE;
              }
              this.cdRef.detectChanges();
            }
          }
        );
    } else {
      this.emitDataToParent();
      this.resetForms();
    }
  }

  /**
   * @method removeValue
   * Remove value of a formControl
   */
  removeValue(formControl: FormControl): void {
    formControl.setValue('');
  }

  private patchIndividualValues(): void {
    this.adminService.getConfig('INDIVIDUAL_FORM_DEFAULT_VALUES').subscribe(res => {
      const patchingValue = res.jsonValue;
      const newDefaultValues = {
        onboardingMode: patchingValue['onboardingMode'],
        paymentModes: [patchingValue['paymentMode']],
        phoneNumbers: [patchingValue['phoneNumber']],
        bankAccounts: [patchingValue['bankAccount']],
        productServiceComplexity: patchingValue['productServiceComplexity'],
      };

      Object.entries(newDefaultValues).forEach(entry => {
        const [key, value] = entry;
        if (typeof value === 'string') {
          this.individualFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        } else if (typeof value === 'object' && !value.includes('') && !value.includes(undefined)) {
          this.individualFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        }
        this.individualFormGroup.markAsPristine();
      });
    });
  }

  private patchIndividualCRPValues(): void {
    this.adminService.getConfig('CRP_INDIVIDUAL_FORM_DEFAULT_VALUES').subscribe(res => {
      const patchingValue = res.jsonValue;
      const newDefaultValues = {
        status: patchingValue['status'],
        phoneNumbers: [patchingValue['phoneNumber']],
        bankAccounts: [patchingValue['bankAccount']],
      };
      Object.entries(newDefaultValues).forEach(entry => {
        const [key, value] = entry;
        if (typeof value === 'string') {
          this.individualFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        } else if (typeof value === 'object' && !value.includes('') && !value.includes(undefined)) {
          this.individualFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        }
        this.individualFormGroup.markAsPristine();
      });
    });
  }

  private patchCorporateValues(): void {
    this.adminService.getConfig('CORPORATE_FORM_DEFAULT_VALUES').subscribe(res => {
      const patchingValue = res.jsonValue;
      const newDefaultValues = {
        onboardingMode: patchingValue['onboardingMode'],
        paymentModes: [patchingValue['paymentMode']],
        phoneNumbers: [patchingValue['phoneNumber']],
        bankAccounts: [patchingValue['bankAccount']],
        productServiceComplexity: patchingValue['productServiceComplexity'],
      };
      Object.entries(newDefaultValues).forEach(entry => {
        const [key, value] = entry;
        if (typeof value === 'string') {
          this.corporateFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        } else if (typeof value === 'object' && !value.includes('') && !value.includes(undefined)) {
          this.corporateFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        }
        this.corporateFormGroup.markAsPristine();
      });
    });
  }

  private patchCorporateCRPValues(): void {
    this.adminService.getConfig('CRP_CORPORATE_FORM_DEFAULT_VALUES').subscribe(res => {
      const patchingValue = res.jsonValue;
      const newDefaultValues = {
        status: patchingValue['status'],
        phoneNumbers: [patchingValue['phoneNumber']],
        bankAccounts: [patchingValue['bankAccount']],
      };
      Object.entries(newDefaultValues).forEach(entry => {
        const [key, value] = entry;
        if (typeof value === 'string') {
          this.corporateFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        } else if (typeof value === 'object' && !value.includes('') && !value.includes(undefined)) {
          this.corporateFormGroup.patchValue({
            [key]: value,
          }, { emitEvent: false, onlySelf: true }
          );
        }
        this.corporateFormGroup.markAsPristine();
      });
    });
  }
}
